"use client";

import React, { useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Pencil, Save, X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { BaseEntity, SimpleFieldConfig, FieldValue, EntityWithRelations } from "../types";
import { renderFieldDisplay } from "../utils/field-display-renderer";
import { renderFieldInput } from "../utils/field-input-renderer";
import { EmailAliasesEditor } from "@/features/contacts/components/EmailAliasesEditor";
import { PhoneAliasesEditor } from "@/features/contacts/components/PhoneAliasesEditor";
import { ExpandableTicketDetails } from "./ExpandableTicketDetails";
import { MultiUserCurrentSelections, MultiUserDisplay } from "./MobileDetailModalField.multi-user";
import {
  areMobileDetailModalFieldPropsEqual,
  type MobileDetailModalFieldEqualityProps,
} from "./MobileDetailModalField.equality";

type MobileDetailModalFieldProps<T extends BaseEntity> = MobileDetailModalFieldEqualityProps<T>;

const TEXTAREA_FIELD_TOKENS = ["description", "notes", "content"] as const;

function isTextareaLikeField(fieldId: string, fieldConfig: SimpleFieldConfig): boolean {
  if (fieldConfig.type === "textarea") return true;
  return TEXTAREA_FIELD_TOKENS.some((token) => fieldId.includes(token));
}

function isTicketField(fieldId: string): boolean {
  return fieldId === "ticket_id" || fieldId.includes("ticket");
}

function getTicketIdFromValue(value: FieldValue): string {
  if (typeof value === "object" && value !== null && "id" in (value as Record<string, unknown>)) {
    return String((value as Record<string, unknown>).id);
  }
  return String(value);
}

/**
 * Mobile-optimized field component for detail modals.
 * Handles display and edit modes for a single field.
 *
 * Memoized via `areMobileDetailModalFieldPropsEqual` so editing one field does not re-render siblings.
 */
function MobileDetailModalFieldComponent<T extends BaseEntity>({
  fieldId,
  fieldConfig,
  value,
  item,
  editedValue,
  isFieldEditing,
  canEdit,
  roleName,
  onEdit,
  onSave,
  onCancel,
  onValueChange,
}: MobileDetailModalFieldProps<T>) {
  const handleFieldValueChange = useCallback(
    (newValue: any) => onValueChange(fieldId, newValue),
    [fieldId, onValueChange]
  );

  const handleSaveClick = useCallback(async () => {
    await onSave(fieldId);
  }, [fieldId, onSave]);

  const showEditButton =
    canEdit && !isFieldEditing && roleName !== "client_user" && fieldConfig.editable;

  const isMultiUser = fieldConfig.type === "multi-user";
  const isEmailAliasesField =
    fieldId === "email_aliases" && Boolean((item as EntityWithRelations<T>)?.email);
  const isPhoneAliasesField =
    fieldId === "phone_aliases" && Boolean((item as EntityWithRelations<T>)?.id);

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-2">
        <label className="text-sm font-semibold text-gray-800 dark:text-gray-200 block flex-1 break-words">
          {fieldConfig.label}
        </label>
        {showEditButton && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(fieldId)}
            className="h-8 w-8 p-0 hover:bg-card-100 flex-shrink-0"
            title="Edit"
          >
            <Pencil className="h-4 w-4" />
          </Button>
        )}
      </div>

      {isFieldEditing ? (
        <div className="space-y-3">
          {isMultiUser && <MultiUserCurrentSelections value={editedValue} />}

          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 dark:bg-blue-950/40 dark:border-blue-800">
            <div className="mb-3">
              {renderFieldInput({
                fieldId,
                fieldConfig,
                value: editedValue,
                onValueChange: handleFieldValueChange,
                placeholder: fieldConfig.placeholder,
                fullWidth: true,
                autoFocus: true,
              })}
            </div>

            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleSaveClick}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                <Save className="h-4 w-4 mr-2" />
                Save
              </Button>
              <Button variant="outline" size="sm" onClick={onCancel} className="flex-1">
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div
          className={cn(
            "p-4 bg-card rounded-lg border border-gray-200 dark:border-gray-700 transition-all duration-200 shadow-sm",
            canEdit && "hover:bg-card-50 hover:border-gray-300 dark:hover:border-gray-700 cursor-pointer hover:shadow-md"
          )}
          onClick={() => canEdit && onEdit(fieldId)}
        >
          <div className="min-h-[2.5rem] flex items-start">
            <div className="flex-1 break-words overflow-hidden w-full">
              <div
                className={cn(
                  "text-card-foreground leading-relaxed whitespace-pre-wrap break-words word-break-break-word",
                  isTextareaLikeField(fieldId, fieldConfig)
                    ? "text-sm min-h-[3rem] py-1"
                    : "text-sm"
                )}
              >
                {isMultiUser ? (
                  <MultiUserDisplay value={value} disabled={isFieldEditing} />
                ) : isEmailAliasesField ? (
                  <EmailAliasesEditor
                    contact={item as any}
                    onUpdate={() => {}}
                    isEditing={false}
                    onEditChange={() => {}}
                  />
                ) : isPhoneAliasesField ? (
                  <PhoneAliasesEditor
                    contact={item as any}
                    onUpdate={() => {}}
                    isEditing={false}
                    onEditChange={() => {}}
                  />
                ) : (
                  renderFieldDisplay({
                    fieldId,
                    fieldConfig,
                    value,
                    showIcon: true,
                    disabled: isFieldEditing,
                  })
                )}
              </div>
              {canEdit && (
                <div className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                  <Pencil className="h-3 w-3" />
                  Tap to edit
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {isTicketField(fieldId) && !isFieldEditing && (
        <div className="mt-2 ml-0">
          <ExpandableTicketDetails ticketId={getTicketIdFromValue(value)} />
        </div>
      )}
    </div>
  );
}

export const MobileDetailModalField = React.memo(
  MobileDetailModalFieldComponent,
  areMobileDetailModalFieldPropsEqual
) as typeof MobileDetailModalFieldComponent;
